import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, TooltipText } from '@/components/common';
import { formatDate } from '@/lib/utils';
import { requestMigrationJobsLoadingSelector } from '@/redux/loading/selectors';
import {
  migrationJobsSelector,
  migrationJobsTotalSelector,
  requestMigrationJobsAsync,
} from '@/modules/AspynMigrator/redux';
import { MigrationReport } from '@/modules/AspynMigrator/components';
import { ViewReport } from './components';

const initialPage = 1;
const initialPageSize = 10;

const Jobs = () => {
  const dispatch = useDispatch();
  const jobs = useSelector(migrationJobsSelector);
  const jobsTotal = useSelector(migrationJobsTotalSelector);
  const jobsLoading = useSelector(requestMigrationJobsLoadingSelector);

  const [pageSize, setPageSize] = useState(initialPageSize);
  const [selectedPage, setSelectedPage] = useState(initialPage);

  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  const getJobs = useCallback(() => {
    const params = {
      page: {
        number: selectedPage,
        size: pageSize,
      },
    };
    dispatch(requestMigrationJobsAsync.request(params));
  }, [dispatch, selectedPage, pageSize]);

  const handleOpenReport = useCallback((jobId) => {
    const report = jobs.find(({ id }) => id === jobId)?.report ?? null;

    setSelectedReport(report);
    setIsReportModalOpen(!!report);
  }, [jobs]);

  const handleCloseReport = useCallback(() => {
    setIsReportModalOpen(false);

    // Reset the selected report after closing the modal
    const timer = setTimeout(() => setSelectedReport(null), 300);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    getJobs();
  }, [getJobs]);

  const onPageChange = useCallback(({ selected }) => {
    setSelectedPage(selected);
  }, []);

  const getStatusBadge = (status) => {
    const statusColors = {
      queued: 'bg-blue-100 border border-blue-200 text-blue-800',
      running: 'bg-yellow-100 border border-yellow-200 text-yellow-800',
      completed: 'bg-green-100 border border-green-200 text-green-800',
      failed: 'bg-red-100 border border-red-200 text-red-800',
    };

    return (
      <span className={`inline-flex items-center px-1 py-0.5 text-xs font-medium rounded-md ${statusColors[status] || statusColors.queued}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const headRows = [
    { value: 'Job ID' },
    { value: 'Title' },
    { value: 'Status' },
    { value: 'Started At' },
    { value: 'Finished At' },
    { value: 'Report' },
    { value: 'Created At' },
  ];

  const jobRows = jobs.map((job) => ([
    {
      value: (
        <code className="inline-flex items-center px-1 py-0.5 text-xs font-medium text-nowrap rounded-md bg-gray-100 border border-gray-200 text-gray-800">{job.id}</code>
      ),
    },
    { value: job.title },
    { value: getStatusBadge(job.status) },
    {
      value: job.started_at ? (
        <TooltipText
          id={`${job.id}:started_at`}
          text={formatDate(job.started_at, formatDate.FORMAT_DISPLAY)}
          message={formatDate(job.started_at, formatDate.FORMAT_TIMESTAMP_DISPLAY)}
        />
      ) : 'n/a',
    },
    {
      value: job.finished_at ? (
        <TooltipText
          id={`${job.id}:finished_at`}
          text={formatDate(job.finished_at, formatDate.FORMAT_DISPLAY)}
          message={formatDate(job.finished_at, formatDate.FORMAT_TIMESTAMP_DISPLAY)}
        />
      ) : 'n/a',
    },
    {
      value: job.report ? (
        <ViewReport
          jobId={job.id}
          onClick={handleOpenReport}
        />
      ) : 'n/a',
    },
    {
      value: (
        <TooltipText
          id={`${job.id}:created_at`}
          text={formatDate(job.created_at, formatDate.FORMAT_DISPLAY)}
          message={formatDate(job.created_at, formatDate.FORMAT_TIMESTAMP_DISPLAY)}
        />
      ),
    },
  ]));

  return (
    <div className="flex flex-col w-full h-full p-8">
      <div className="mb-10">
        <h1 className="text-4xl whitespace-nowrap text-gray-900">Migration Jobs</h1>
      </div>

      <div className="grow">
        <Table
          isStickyHeader={true}
          className="relative w-full border-separate border-spacing-0"
          loading={jobsLoading}
          thead={{
            rows: headRows,
          }}
          tbody={{
            rows: jobRows,
          }}
          paginator={{
            pageSize,
            setPageSize,
            onPageChange,
            selectedPage,
            initialPage,
            rowCount: jobsTotal,
          }}
          wrapper={{
            className: 'overflow-y-visible',
          }}
        />
      </div>

      <MigrationReport
        report={selectedReport}
        isOpened={isReportModalOpen}
        onClose={handleCloseReport}
      />
    </div>
  );
};

export default Jobs;
